import type { FetchExistingCustomerRequest } from '../models/opportunity.model';
import {
  CORPORATE_PAN_ENTITY_CHARS,
  COMPANY_PAN_PATTERN,
  INDIVIDUAL_PAN_PATTERN,
  maskAadhaarInput,
  maskIndianMobileInput,
  maskPanInput,
} from './pan-aadhaar-mask.util';

import {
  OPPORTUNITY_SEARCH_INVALID_AADHAAR,
  OPPORTUNITY_SEARCH_INVALID_CIN_GSTIN,
  OPPORTUNITY_SEARCH_INVALID_CONTACT,
  OPPORTUNITY_SEARCH_DOB_REQUIRES_NAME,
  OPPORTUNITY_SEARCH_INVALID_NAME,
  OPPORTUNITY_SEARCH_INVALID_ORGANISATION,
  OPPORTUNITY_SEARCH_NAME_REQUIRES_DOB,
  OPPORTUNITY_SEARCH_INVALID_PAN_CORPORATE,
  OPPORTUNITY_SEARCH_INVALID_PAN_INDIVIDUAL,
} from '../constants/opportunity-fieldspec-messages';
const AADHAAR_PATTERN = /^\d{12}$/;
const INDIAN_MOBILE_PATTERN = /^[6-9]\d{9}$/;
const INDIVIDUAL_NAME_PATTERN = /^[A-Za-z ]*$/;
const ORGANIZATION_NAME_PATTERN = /^[A-Za-z0-9 &.,'()-]+$/;
const GSTIN_PATTERN =
  /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const CIN_PATTERN =
  /^[LUlu]\d{5}[A-Za-z]{2}\d{4}[A-Za-z]{3}\d{6}$/;

export type ExistingCustomerSearchValidationResult =
  | { ok: true }
  | { ok: false; message: string };

function invalid(message: string): ExistingCustomerSearchValidationResult {
  return { ok: false, message };
}

function trimStringField(value: unknown): unknown {
  return typeof value === 'string' ? value.trim() : value;
}

function trimStringFields(
  target: Record<string, unknown>,
  keys: readonly string[],
): void {
  for (const key of keys) {
    target[key] = trimStringField(target[key]);
  }
}

function maskStringFields(
  target: Record<string, unknown>,
  keys: readonly string[],
  mask: (raw: string) => string,
): void {
  for (const key of keys) {
    const raw = target[key];
    if (typeof raw === 'string' && raw.trim()) {
      target[key] = mask(raw);
    }
  }
}

function normalizeCorporateSearchValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...values };
  trimStringFields(out, [
    'searchByOrganizationName',
    'textField',
    'organizationName',
    'gSTNumber',
    'gstNumber',
    'textField3',
    'incorporationRegistrationNumber',
    'textField4',
  ]);
  maskStringFields(out, ['panNumber', 'textField2'], (raw) =>
    maskPanInput(raw, true, CORPORATE_PAN_ENTITY_CHARS),
  );
  for (const key of ['gSTNumber', 'gstNumber', 'textField3'] as const) {
    const raw = out[key];
    if (typeof raw === 'string' && raw.trim()) {
      out[key] = raw.trim().toUpperCase();
    }
  }
  return out;
}

function normalizeIndividualSearchValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...values };
  trimStringFields(out, [
    'searchByName',
    'textField',
    'name',
    'customerName',
  ]);
  maskStringFields(out, ['panNumber', 'textField3', 'pan'], (raw) =>
    maskPanInput(raw, false),
  );
  maskStringFields(
    out,
    ['aadharNumber', 'textField2', 'aadhar'],
    maskAadhaarInput,
  );
  maskStringFields(
    out,
    ['contactNumber', 'phone', 'contact'],
    maskIndianMobileInput,
  );
  return out;
}

/** Normalize PAN / Aadhaar / mobile values from pack search form raw values. */
export function normalizeExistingCustomerSearchFormValues(
  applicantType: 'individual' | 'corporate',
  values: Record<string, unknown>,
): Record<string, unknown> {
  return applicantType === 'corporate'
    ? normalizeCorporateSearchValues(values)
    : normalizeIndividualSearchValues(values);
}

function validateOrganizationName(
  name: string | undefined,
): ExistingCustomerSearchValidationResult {
  if (!name) {
    return { ok: true };
  }
  if (name.length < 2 || name.length > 100) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_ORGANISATION);
  }
  if (!ORGANIZATION_NAME_PATTERN.test(name)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_ORGANISATION);
  }
  return { ok: true };
}

function validateIndividualName(
  name: string | undefined,
): ExistingCustomerSearchValidationResult {
  if (!name) {
    return { ok: true };
  }
  if (name.length < 2) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_NAME);
  }
  if (!INDIVIDUAL_NAME_PATTERN.test(name)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_NAME);
  }
  return { ok: true };
}

function validateCorporateSearchRequest(
  request: FetchExistingCustomerRequest,
): ExistingCustomerSearchValidationResult {
  const orgCheck = validateOrganizationName(request.organizationName);
  if (!orgCheck.ok) {
    return orgCheck;
  }
  if (request.panNumber && !COMPANY_PAN_PATTERN.test(request.panNumber)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_PAN_CORPORATE);
  }
  if (request.gstNumber && !GSTIN_PATTERN.test(request.gstNumber.toUpperCase())) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_CIN_GSTIN);
  }
  if (
    request.incorporationRegistrationNumber &&
    !CIN_PATTERN.test(request.incorporationRegistrationNumber)
  ) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_CIN_GSTIN);
  }
  return { ok: true };
}

function validateNameDobPairing(
  request: FetchExistingCustomerRequest,
): ExistingCustomerSearchValidationResult {
  const name = request.customerName?.trim();
  const dob = request.customerDob?.trim();
  const hasName = Boolean(name);
  const hasDob = Boolean(dob);
  if (hasName && !hasDob) {
    return invalid(OPPORTUNITY_SEARCH_NAME_REQUIRES_DOB);
  }
  if (hasDob && !hasName) {
    return invalid(OPPORTUNITY_SEARCH_DOB_REQUIRES_NAME);
  }
  return { ok: true };
}

function validateIndividualSearchRequest(
  request: FetchExistingCustomerRequest,
): ExistingCustomerSearchValidationResult {
  const pairing = validateNameDobPairing(request);
  if (!pairing.ok) {
    return pairing;
  }
  const nameCheck = validateIndividualName(request.customerName);
  if (!nameCheck.ok) {
    return nameCheck;
  }
  if (request.aadharNumber && !AADHAAR_PATTERN.test(request.aadharNumber)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_AADHAAR);
  }
  if (request.panNumber && !INDIVIDUAL_PAN_PATTERN.test(request.panNumber)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_PAN_INDIVIDUAL);
  }
  if (request.contactNumber && !INDIAN_MOBILE_PATTERN.test(request.contactNumber)) {
    return invalid(OPPORTUNITY_SEARCH_INVALID_CONTACT);
  }
  return { ok: true };
}

/** Validate configured formats for any non-empty search criteria. */
export function validateExistingCustomerSearchRequest(
  request: FetchExistingCustomerRequest,
): ExistingCustomerSearchValidationResult {
  return request.applicantType === 'corporate'
    ? validateCorporateSearchRequest(request)
    : validateIndividualSearchRequest(request);
}
